import React, { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { FirebaseContext } from '.';
import { useCollectionData, useDocumentData } from 'react-firebase-hooks/firestore';
import firebase from 'firebase/app';
import { useParams } from 'react-router-dom';
import { ReadlistData, ReadlistItem, User } from './types';
import Loading from './Loading';
import ConditionalDisplay from './ConditionalDisplay';

const trim = (text: string, length: number = 50): string => {
  return text.length > length ? text.substring(0, length - 3) + "..." : text;
}

interface ReadlistAddItemProps {
  readlistItemCollection: firebase.firestore.CollectionReference<firebase.firestore.DocumentData>,
  readlistId: string,
};

const ReadlistAddItem = ({ readlistItemCollection, readlistId }: ReadlistAddItemProps) => {
  const { auth, analytics } = useContext(FirebaseContext);
  const [ title, setTitle ] = useState<string>('');
  const [ link, setLink ] = useState<string>('');

  const addReadlistItem = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    if (auth.currentUser) {
      const { uid } = auth.currentUser;

      try {
        await readlistItemCollection.add({
          title,
          link,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          creator: uid,
          readlistId,
          isRead: false
        });
      } catch(error) {
        console.log('Error: ', error);
        analytics.logEvent('exception', { description: 'Adding new readlist item failed', fatal: false, error });
      }
  
      setTitle('');
      setLink('');
    }
  }

  return (
    <form className='row my-5' onSubmit={ addReadlistItem }>
      <div className='col-12'>
        <label>Title:</label>
        <input className='form-control' value={ title } onChange={ (e) => setTitle(e.target.value) } />
      </div>
      <div className='col-12'>
        <label>Link:</label>
        <input className='form-control' value={ link } onChange={ (e) => setLink(e.target.value) } />
      </div>
      <div className='col-12 d-flex justify-content-center my-3'>
        <button className='btn btn-primary col-md-5 col' type='submit' disabled={ !!!title || !!!link }>Add new readlist item</button>
      </div>
    </form>
  );
};

interface ReadlistItemElementProps {
  readlistItem: ReadlistItem,
  markRead: () => void,
};

const dateFormatter = new Intl.DateTimeFormat('default', {
  year: 'numeric', month: 'numeric', day: 'numeric',
  hour: 'numeric', minute: 'numeric', second: 'numeric',
  hour12: false
});

const ReadlistItemElement = ({ readlistItem, markRead }: ReadlistItemElementProps) => {
  return (
    <li className={ (readlistItem.isRead ? 'read ' : '') + 'list-group-item' }>
      <p>
        <strong onClick={ markRead }>{ readlistItem.title }</strong> - { readlistItem.createdAt ? dateFormatter.format(readlistItem.createdAt.toDate()) : '' }
      </p>
      <a href={ readlistItem.link }>{ trim(readlistItem.link) }</a>
    </li>
  );
};

interface ReadlistIdProps {
  readlistId: string,
};

const ReadlistName = ({ readlistId }: ReadlistIdProps) => {
  const { auth, firestore, analytics } = useContext(FirebaseContext);
  const readlistRef = firestore.collection('readlists').doc(readlistId);
  const [ readlist ] = useDocumentData<ReadlistData>(readlistRef);
  const [ readlistName, setReadlistName ] = useState<string>('');
  const [ isEditing, setIsEditing ] = useState<boolean>(false);

  useEffect(() => {
    if (readlist) {
      setReadlistName(readlist.name);
    }
  }, [ readlist ]);

  const changeReadlistName = async (): Promise<void> => {
    try {
      await readlistRef.set({ name: readlistName, owner: readlist?.owner, collaborators: readlist?.collaborators });
    } catch(error) {
      console.log('Error: ', error);
      analytics.logEvent('exception', { description: 'Changing readlist name failed', fatal: false, error });
    }

    setIsEditing(false);
  }

  return (
    <ConditionalDisplay condition={ isEditing && auth.currentUser?.uid === readlist?.owner } 
      trueDisplay={ 
        <div className='row my-3'>
          <div className='col-md-8 col-12'>
            <input className='form-control ' type='text' value={ readlistName } onChange={ e => setReadlistName(e.target.value) }/>
          </div>
          <div className='col-12 col-md-2'>
          <button className='btn btn-success w-100' onClick={ changeReadlistName }><i className='fas fa-save' /></button>
          </div>
          <div className='col-12 col-md-2'>
          <button className='btn btn-warning w-100' onClick={ () => setIsEditing(false) }><i className='fas fa-ban' /></button>
          </div>
        </div> } 
      falseDisplay={ <h1 onClick={ () => setIsEditing(true) }>{ readlist?.name }  { auth.currentUser?.uid === readlist?.owner ? <i className='fas fa-edit' /> : null }</h1> } />
  );
};

const getUserByMail = (users: User[], mail: string): User | undefined => {
  return users.find(user => user.mail === mail);
}

function isNotUndefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

const ReadlistCollaborators = ({ readlistId }: ReadlistIdProps) => {
  const { auth, firestore, analytics } = useContext(FirebaseContext);
  const usersRef = firestore.collection('users');
  const [ users ] = useCollectionData<User>(usersRef, { idField: 'id' });
  const readlistRef = firestore.collection('readlists').doc(readlistId);
  const [ readlist ] = useDocumentData<ReadlistData>(readlistRef);
  const [ collaboratorsString, setCollaboratorsString ] = useState<string>('');
  const [ isEditing, setIsEditing ] = useState<boolean>(false);

  useEffect(() => {
    if (readlist && users) {
      const collabs = users.filter(user => readlist.collaborators.findIndex(collab => collab === user.uid) !== -1);
      setCollaboratorsString(collabs.map(user => user.mail).join(', '));
    }
  }, [ readlist, users ]);

  const changeCollaborators = async (): Promise<void> => {
    try {
      if (users && readlist) {
        const collaboratorUIds = collaboratorsString.split(',').map(mail => mail.trim()).map(mail => getUserByMail(users, mail)).filter(isNotUndefined).map(user => user.uid);

        await readlistRef.set({ name: readlist.name, owner: readlist.owner, collaborators: collaboratorUIds });
        await readlistRef.get();
        await usersRef.get();
      }
    } catch(error) {
      console.log('Error: ', error);
      analytics.logEvent('exception', { description: 'Changing readlist collaborators failed', fatal: false, error });
    }

    setIsEditing(false);
  }

  return (
    <ConditionalDisplay condition={ isEditing && auth.currentUser?.uid === readlist?.owner } 
      trueDisplay={ 
        <div className='row my-3'>
          <div className='col-md-8 col-12'>
            <input className='form-control ' type='text' value={ collaboratorsString } onChange={ e => setCollaboratorsString(e.target.value) } />
          </div>
          <div className='col-12 col-md-2'>
            <button className='btn btn-success w-100' onClick={ changeCollaborators }><i className='fas fa-save' /></button>
          </div>
          <div className='col-12 col-md-2'>
            <button className='col btn btn-warning w-100' onClick={ () => setIsEditing(false) }><i className='fas fa-ban' /></button>
          </div>
        </div> } 
      falseDisplay={ <div onClick={ () => setIsEditing(true) }><h3>Collaborators:</h3> { collaboratorsString.split(',').map(mail => mail.trim()).map(mail =>  (<span className='badge bg-light text-dark'>{ mail }</span>)) } { auth.currentUser?.uid === readlist?.owner ? <i className='fas fa-edit' /> : null }</div> } />
  );
};

const Readlist = () => {
  const { readlistId } = useParams<{ readlistId: string }>();
  const { firestore, analytics } = useContext(FirebaseContext);
  const readlistItemCollection = firestore.collection('readlist');
  const query = readlistItemCollection.where('readlistId', '==', readlistId).orderBy('createdAt', 'desc').limitToLast(25);
  const [ readlistItems, loading, error ] = useCollectionData<ReadlistItem>(query, { idField: 'id' });
  
  console.log('Readlist ID: ', readlistId);

  if (error) {
    console.log(error);
    analytics.logEvent('exception', { description: 'Fetching readlist failed', fatal: false, error });
  }

  const dummy = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    dummy.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ readlistItems ]);

  const markRead = async (readlistItem: ReadlistItem): Promise<void> => {
    try {
      await readlistItemCollection.doc(readlistItem.id).update({ isRead: true });
    } catch(error) {
      console.log('Error: ', error);
      analytics.logEvent('exception', { description: 'Updating readlist item failed', fatal: false, error });
    }
  };

  return (
    <div className='col'>
      <ReadlistName readlistId={ readlistId } />
      <ReadlistCollaborators readlistId={ readlistId } />
      <ReadlistAddItem readlistItemCollection={ readlistItemCollection } readlistId={ readlistId } />
      <span ref={ dummy } />
      <Loading loading={ loading } />
      { (!!!readlistItems || readlistItems.length === 0) && !!!loading && <div className='alert alert-warning'>Seems that your readlist is empty. Start adding items here.</div> }
      <ul className='list-group m-0 p-0'>
        { readlistItems && readlistItems.map(readlistItem => <ReadlistItemElement key={ readlistItem.id } readlistItem={ readlistItem } markRead={ () => markRead(readlistItem) } />)}
      </ul>
    </div>
  );
};

export default Readlist;